/**
 * État partagé entre les onglets d'une origine, dans le `localStorage` (une clé par clé publique).
 * Rien n'y est écrit avant `startSession()`.
 */

import type { RrwebEvent } from '@belvdr/shared'
import { migrateLegacyKey } from './legacy-storage.js'
import { uuidv7 } from './uuid.js'

/** Sans interaction pendant ce délai, la session se termine (cf. PRD). */
export const SESSION_IDLE_TIMEOUT_MS = 30 * 60_000
/** Durée maximale d'une session : au-delà, l'activité suivante en démarre une nouvelle. */
export const SESSION_MAX_DURATION_MS = 24 * 60 * 60_000

/** Session en cours, telle que la voient tous les onglets. */
export type SharedSession = {
  id: string
  /** Début de la session, en ms depuis l'epoch. */
  startedAt: number
  /** Dernière interaction de l'utilisateur, tous onglets confondus. */
  lastActivityAt: number
  /**
   * Session refusée par l'API, devenue trop volumineuse : elle n'est plus enregistrée, dans aucun
   * onglet, jusqu'à la suivante (inactivité, 24 h, `forceNew`).
   */
  capped?: boolean
}

/**
 * Identité du navigateur : son `distinctId` et, une fois identifié, l'utilisateur à qui il
 * appartient. Un `distinctId` n'appartient jamais qu'à un seul utilisateur.
 */
export type Identity = {
  /** Identifiant du navigateur (UUIDv7) : conservé par `stopSession()`, changé par `reset()`. */
  distinctId: string
  /** `userId` passé à `identify()` ou `startSession({ userId })` ; absent : anonyme. */
  userId?: string
}

export type SharedState = Identity & {
  session: SharedSession | null
}

/** Lecture et écriture de l'état partagé. */
export type SessionStore = {
  read(): SharedState | null
  write(state: SharedState): void
  /**
   * Mode erreurs démarré (partagé entre onglets, à part de l'état de session) : chaque page le
   * démarre avec `startErrorTracking()`, `stopErrorTracking()` l'arrête dans tous les onglets.
   */
  readErrorTracking(): boolean
  writeErrorTracking(started: boolean): void
}

/**
 * État dans le `localStorage`, sous `belvdr:<clé publique>`. Si le stockage est inaccessible
 * (iframe isolée, cookies bloqués, quota) ou illisible, l'état est gardé en mémoire : la page
 * est enregistrée, sans partage entre onglets ni reprise au rechargement.
 *
 * Au premier accès, l'état d'avant le renommage (`belvedere:<clé publique>`) est repris, puis
 * effacé (cf. `migrateLegacyKey`).
 */
export function localSessionStore(publicKey: string): SessionStore {
  const key = `belvdr:${publicKey}`
  const errorTrackingKey = `belvdr:errors:${publicKey}`
  let fallback: SharedState | null = null
  let errorTrackingFallback = false
  let migrated = false
  /** Reprise de l'ancienne clé, une fois, au premier accès (jamais avant un appel au SDK). */
  function migrate() {
    if (migrated) return
    migrated = true
    try {
      migrateLegacyKey(localStorage, `belvedere:${publicKey}`, key)
    } catch {
      // `localStorage` inaccessible : rien à reprendre.
    }
  }
  return {
    read() {
      migrate()
      try {
        const raw = localStorage.getItem(key)
        return raw === null ? null : parseState(raw)
      } catch {
        return fallback
      }
    },
    write(state) {
      migrate()
      fallback = state
      try {
        localStorage.setItem(key, JSON.stringify(state))
      } catch {
        // État gardé en mémoire.
      }
    },
    readErrorTracking() {
      try {
        return localStorage.getItem(errorTrackingKey) === '1'
      } catch {
        return errorTrackingFallback
      }
    },
    writeErrorTracking(started) {
      errorTrackingFallback = started
      try {
        if (started) localStorage.setItem(errorTrackingKey, '1')
        else localStorage.removeItem(errorTrackingKey)
      } catch {
        // État gardé en mémoire.
      }
    },
  }
}

/** Relit l'état écrit par le SDK ; toute autre valeur est ignorée (et sera remplacée). */
function parseState(raw: string): SharedState | null {
  const state = JSON.parse(raw) as {
    distinctId?: unknown
    userId?: unknown
    session?: Partial<SharedSession>
  } | null
  if (typeof state?.distinctId !== 'string') return null
  const session = state.session
  const valid =
    typeof session?.id === 'string' &&
    typeof session.startedAt === 'number' &&
    typeof session.lastActivityAt === 'number'
  return {
    ...identityOf({ distinctId: state.distinctId, userId: state.userId }),
    session: valid ? (session as SharedSession) : null,
  }
}

/** L'identité seule, sans `userId` s'il est anonyme. */
export function identityOf(state: { distinctId: string; userId?: unknown }): Identity {
  return typeof state.userId === 'string'
    ? { distinctId: state.distinctId, userId: state.userId }
    : { distinctId: state.distinctId }
}

/** Nouvelle identité de navigateur : anonyme, ou déjà rattachée à `userId`. */
export function newIdentity(now: number, userId?: string): Identity {
  return userId === undefined ? { distinctId: uuidv7(now) } : { distinctId: uuidv7(now), userId }
}

export function createSession(now: number): SharedSession {
  return { id: uuidv7(now), startedAt: now, lastActivityAt: now }
}

/** Une session est active tant qu'elle a moins de 24 h et une interaction de moins de 30 min. */
export function isActive(
  session: Pick<SharedSession, 'startedAt' | 'lastActivityAt'>,
  now: number,
): boolean {
  return (
    now - session.lastActivityAt < SESSION_IDLE_TIMEOUT_MS &&
    now - session.startedAt < SESSION_MAX_DURATION_MS
  )
}

/** rrweb : `IncrementalSnapshot` et ses sources issues d'une action de l'utilisateur. */
const INCREMENTAL_SNAPSHOT = 3
const USER_SOURCES = new Set([
  1, // MouseMove
  2, // MouseInteraction
  3, // Scroll
  4, // ViewportResize
  5, // Input
  6, // TouchMove
  7, // MediaInteraction
  12, // Drag
])

/** Événement produit par une action de l'utilisateur (et non par la page elle-même). */
export function isInteraction(event: RrwebEvent): boolean {
  if (event.type !== INCREMENTAL_SNAPSHOT) return false
  const source = (event.data as { source?: unknown } | undefined)?.source
  return typeof source === 'number' && USER_SOURCES.has(source)
}
